import React, { useMemo } from 'react';
import { Box, Typography, Checkbox, useTheme } from '@mui/material';
import { getNumberComma } from '../../../utils/numberUtils';

const imptColorMap = { High: 'error.main', Medium: 'success.main', Low: '#4A4A4A' };
const imptItems = [{ value: 'High', label: 'H' }, { value: 'Medium', label: 'M' }, { value: 'Low', label: 'L' }];

const InfoCard = ({ title, mainColor, data, isSelected, border, shadow, onClick, isTotal = false }) => (
  <Box onClick={onClick} sx={{
    display: 'flex',
    minWidth: '390px', 
    height: '92px', 
    borderRadius: '0px', 
    cursor: onClick ? 'pointer' : 'default', 
    userSelect: 'none',
    backgroundColor: 'background.paper', 
    border: border, 
    boxShadow: shadow, 
    transition: 'all 0.15s ease', 
    '&:hover': onClick ? { borderColor: mainColor, backgroundColor: `${mainColor}04` } : {}
  }}>
    <Box sx={{
      width: '30px',
      backgroundColor: mainColor,
      color: '#fff',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexShrink: 0,
      px: '4px'
    }}>
      <Typography sx={{ 
        fontSize: '1.2rem', 
        fontWeight: 900, 
        wordBreak: 'break-all',
        whiteSpace: 'normal',
        textAlign: 'center',
        lineHeight: 1.1,
        letterSpacing: isTotal ? '0px' : 'inherit'
      }}>
        {title}
      </Typography>
    </Box>

    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: '6px 10px', justifyContent: 'center' }}>
      {!isTotal && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', height: '0px', mb: '4px' }}>
          <Checkbox 
            size="small" 
            checked={isSelected} 
            readOnly 
            sx={{ 
              p: 0, 
              mt: '-6px',
              mr: '-10px',
              color: mainColor === '#4A4A4A' ? '#4A4A4A80' : 'inherit',
              '&.Mui-checked': { color: mainColor } 
            }} 
          />
        </Box>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        {/* 우측 내부 좌측 분할 영역 */}
        <Box sx={{ flex: 1.15, display: 'flex', flexDirection: 'column', gap: '2px'}}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Typography sx={{ fontSize: '0.8rem', color: 'text.primary', flexShrink: 0 }}>총매출액:</Typography>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.primary', textAlign: 'right', whiteSpace: 'nowrap' }}>
              {getNumberComma(data.sales)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Typography sx={{ fontSize: '0.8rem', color: 'text.primary', flexShrink: 0 }}>정산전 매출이익:</Typography>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary', textAlign: 'right', whiteSpace: 'nowrap' }}>
              {getNumberComma(data.purchase)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Typography sx={{ fontSize: '0.8rem', color: 'text.primary', flexShrink: 0 }}>
              {data.status === 'before' ? '예상 매출 이익' : '최종 매출 이익'}:
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: mainColor, textAlign: 'right', whiteSpace: 'nowrap' }}>
              {getNumberComma(data.profit)}
            </Typography>
          </Box>
        </Box>
        
        {/* 우측 내부 우측 분할 영역 */}
        <Box sx={{ flex: 0.85, display: 'flex', flexDirection: 'column', gap: '2px', pl: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Typography sx={{ fontSize: '0.8rem', color: 'text.primary', flexShrink: 0 }}>협업이익:</Typography>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: 'text.primary', whiteSpace: 'nowrap' }}>
              {getNumberComma(data.benefits)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Typography sx={{ fontSize: '0.8rem', color: 'text.primary', flexShrink: 0 }}>협업비용:</Typography>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: 'text.primary', whiteSpace: 'nowrap' }}>
              {getNumberComma(data.cost)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: mainColor, whiteSpace: 'nowrap' }}>
              ({getNumberComma(data.margin)}%)
            </Typography>
            
            <Typography sx={{ fontSize: '0.8rem', color: 'text.primary', flexShrink: 0 }}></Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
);

const ShareSearchImpt = ({ formik, totals }) => {
  const theme = useTheme();
  const localPrjImpt = formik.values?.searchPrjImpt || [];

  const totalCardTitle = useMemo(() => {
    if (localPrjImpt.length === 0 || localPrjImpt.length === imptItems.length) {
      return '전체';
    }
    const activeLabels = imptItems
      .filter(item => localPrjImpt.includes(item.value))
      .map(item => item.label);
    
    return activeLabels.join('+');
  }, [localPrjImpt]);

  const dynamicTotalData = useMemo(() => {
    if (localPrjImpt.length === 0) {
      return {
        sales: totals?.totalSales || 0,
        purchase: totals?.totalPurchase || 0,
        margin: totals?.totalMargin || '0.00',
        benefits: totals?.collaborationBenefits || 0,
        cost: totals?.collaborationCost || 0,
        profit: totals?.totalProfit || 0,
        status: totals?.calcStatus || 'before',
      };
    }

    let sales = 0;
    let purchase = 0;
    let benefits = 0;
    let cost = 0;
    let profit = 0;

    localPrjImpt.forEach((key) => {
      const target = totals?.[key];
      if (target) {
        sales += target.sales || 0;
        purchase += target.salesProfitBefore || 0;
        benefits += target.collaborationBenefits || 0;
        cost += target.collaborationCost || 0;
        profit += target.finalSalesProfit || 0;
      }
    });

    const margin = sales > 0 ? ((profit / sales) * 100).toFixed(2) : '0.00';

    return {
      sales,
      purchase,
      margin,
      benefits,
      cost,
      profit,
      status: totals?.calcStatus || 'before',
    };
  }, [localPrjImpt, totals]);

  const handleCardClick = (value) => {
    const nextImpt = localPrjImpt.includes(value) ? localPrjImpt.filter((item) => item !== value) : [...localPrjImpt, value];
    formik.setFieldValue('searchPrjImpt', nextImpt);
  };

  const pColor = theme.palette.primary.main;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, flexWrap: 'wrap', width: '100%', mt: 1, mb: 3 }}>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
        <InfoCard
          title={totalCardTitle} 
          mainColor={pColor} 
          isSelected={true} 
          isTotal={true} 
          border={`1px solid ${pColor}`} 
          shadow={`3px 3px 4px rgba(0, 0, 0, 0.15)`}
          data={dynamicTotalData} 
        />

        {imptItems.map((item) => {
          const isSelected = localPrjImpt.includes(item.value);
          const rawColor = imptColorMap[item.value];
          const mColor = rawColor.startsWith('#') 
            ? rawColor 
            : (rawColor.split('.').reduce((acc, part) => acc?.[part], theme.palette) || pColor);

          const d = totals?.[item.value];

          return (
            <InfoCard
              key={item.value}
              title={item.label} 
              mainColor={mColor} 
              isSelected={isSelected}
              border={`1px solid ${isSelected ? mColor : theme.palette.divider}`} 
              shadow={isSelected ? `3px 3px 4px rgba(0, 0, 0, 0.18)` : `3px 3px 4px rgba(0, 0, 0, 0.1)`}
              onClick={() => handleCardClick(item.value)}
              data={{ sales: d?.sales || 0, purchase: d?.salesProfitBefore || 0, margin: d?.salesProfitMargin || '0.00', benefits: d?.collaborationBenefits || 0, cost: d?.collaborationCost || 0, profit: d?.finalSalesProfit || 0, status: d?.calcStatus || 'before' }}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default ShareSearchImpt;
